import type { AuthContextValue } from "@/contexts/AuthContext";
import {
  type OrgLocationPickerOption,
} from "@/lib/locations/orgLocationPickerOptions";
import {
  cardMutedTextClass,
  cardTitleTextClass,
  compactInputClass,
  ghostBtnClass,
  secondarySurfaceClass,
} from "@/components/common/surfaceClasses";
import {
  DAY_OPTIONS,
  type DashboardMetrics,
  type DayRange,
} from "@/components/dashboard/orgDashboardMetrics";
import {
  OrgDashboardBarChart,
  OrgDashboardStatCard,
  OrgDashboardTooltip,
} from "@/components/dashboard/OrgDashboardUi";

/**
 * Header card for OrgDashboard \u2014 extracted to keep the parent under the
 * file-size hygiene hard limit. Carries the org name + scoped org id, the
 * location selector + day-range pills + Refresh button, and the four KPI
 * tiles + bar chart.
 *
 * The outer chrome is aligned to the SectionCard recipe (\u00a7K2) but stays
 * inlined here because the rich h2 title + three-line subtitle don't fit
 * SectionCard's plain-string `title` prop.
 */
export function OrgDashboardHeaderSection(props: {
  auth: AuthContextValue;
  orgName: string | null;
  orgId: string | null;
  locationId: string | null;
  locationOptions: OrgLocationPickerOption[];
  locationSaving: boolean;
  setMyLocation: (id: string | null) => void | Promise<void>;
  days: DayRange;
  setDays: (d: DayRange) => void;
  loading: boolean;
  fetchData: () => void | Promise<void>;
  error: string | null;
  metrics: DashboardMetrics | null;
  kbGapCount: number | null;
  sentRate: number | null;
}) {
  const {
    auth,
    orgName,
    orgId,
    locationId,
    locationOptions,
    locationSaving,
    setMyLocation,
    days,
    setDays,
    loading,
    fetchData,
    error,
    metrics,
    kbGapCount,
    sentRate,
  } = props;
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/45">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className={`text-xl font-semibold ${cardTitleTextClass}`}>{orgName ?? "Organization Dashboard"}</h2>
          <div className={`text-xs mt-0.5 font-mono ${cardMutedTextClass}`}>{orgId ?? "\u2014"}</div>
          <div className={`flex items-center gap-1 mt-1 text-xs ${cardMutedTextClass}`}>
            App-submitted leads only
            <OrgDashboardTooltip text="Stats, charts, and breakdowns in this section reflect leads submitted through this app only. They do not include direct CRM activity, manual entries, or other lead sources." />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className={`text-xs ${compactInputClass}`}
            value={locationId ?? ""}
            disabled={locationSaving}
            onChange={(e) => void setMyLocation(e.target.value || null)}
            title="Set your active location to see location-specific pipeline hygiene"
          >
            <option value="">My location: all</option>
            {(auth.accessibleLocationIds.length > 0
              ? locationOptions.filter((loc) => auth.accessibleLocationIds.includes(loc.id))
              : locationOptions
            ).map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.label}
              </option>
            ))}
          </select>
          <div className={`inline-flex items-center rounded-full border p-0.5 gap-0.5 ${secondarySurfaceClass}`}>
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                  days === d ? "bg-white text-indigo-700 font-semibold shadow-sm dark:bg-zinc-800 dark:text-indigo-300" : `${cardMutedTextClass} hover:text-gray-700 dark:hover:text-zinc-200`
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => void fetchData()}
            disabled={loading}
            className={`text-xs px-3 py-1.5 disabled:opacity-50 ${ghostBtnClass}`}
          >
            {loading ? "Loading\u2026" : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 dark:bg-red-950/35 dark:border-red-800 dark:text-red-200">{error}</div>
      )}

      {metrics && (
        <>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <OrgDashboardStatCard
              label="Today"
              value={metrics.todayLeads}
              sub={`${metrics.todaySent} sent`}
              accent="border-indigo-100 bg-indigo-50"
            />
            <OrgDashboardStatCard
              label={`Last ${days}d \u2014 total`}
              value={metrics.totalLeads}
              sub={`${metrics.draftLeads} in draft`}
            />
            <OrgDashboardStatCard
              label="Sent to CRM"
              value={metrics.sentLeads}
              sub={sentRate !== null ? `${sentRate}% conversion` : undefined}
              accent="border-emerald-100 bg-emerald-50"
            />
            <OrgDashboardStatCard label="Team members" value={metrics.activeMembers} sub="active" />
          </div>
          {kbGapCount !== null && kbGapCount > 0 ? (
            <div className="mt-3">
              <OrgDashboardStatCard
                label="KB gaps open"
                value={kbGapCount}
                sub="review in Knowledge Base tab"
                accent="border-amber-200 bg-amber-50"
              />
            </div>
          ) : null}

          <div className="mt-5">
            <div className={`text-xs uppercase tracking-wide mb-2 ${cardMutedTextClass}`}>
              Leads created (light) vs. sent (dark) \u2014 last {days} days
            </div>
            <OrgDashboardBarChart data={metrics.dailyBreakdown} days={days} />
          </div>
        </>
      )}

      {!loading && !metrics && !error && (
        <div className={`mt-4 text-sm ${cardMutedTextClass}`}>No lead data available for this organization.</div>
      )}
    </section>
  );
}
